import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Swords, X, Gem, Loader2, PawPrint } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/store';
import { themeOf, ARENA } from '../lib/classTheme';
import { ArenaFloor, Pedestal, Laurel, VersusEmblem } from '../components/ArenaFrame';
import { SkeletonList } from '../components/ui';

export default function FightAccept() {
  const { code } = useParams();
  const nav = useNavigate();
  const { hero } = useAuth();
  const [busy, setBusy] = useState(false);

  const { data: inv, isLoading, error } = useQuery({
    queryKey: ['fightInvite', code],
    queryFn: () => api.get(`/fights/invite/${code}`).then(r => r.data),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  const accept = useMutation({
    mutationFn: () => api.post(`/fights/accept/${code}`),
    onMutate: () => setBusy(true),
    onSuccess: ({ data }) => {
      toast.success('The crowd roars! ⚔️');
      // navigate immediately — do NOT call load() here
      nav(`/fight/${data.fightId}`, { replace: true });
    },
    onError: (e) => { setBusy(false); toast.error(e.message); },
  });

  const decline = useMutation({
    mutationFn: () => api.post(`/fights/decline/${code}`),
    onSuccess: () => { toast('Declined'); nav('/arena'); },
  });

  if (isLoading) return <SkeletonList n={2} h="h-56" />;

  if (error) return (
    <div className="card p-8 text-center">
      <X size={36} className="mx-auto mb-3" style={{ color: ARENA.blood }} />
      <p className="font-display font-bold">{error.message}</p>
      <Link to="/arena" className="btn-primary btn-sm mt-4">Back to Arena</Link>
    </div>
  );

  const t = themeOf(hero?.class_key);
  const ot = themeOf(inv.class_key);

  return (
    <div className="max-w-md mx-auto animate-fade-up">
      <div className="card-arena overflow-hidden">
        <ArenaFloor classKey={hero?.class_key} className="p-6" showCrowd>
          <div className="text-center">
            <Laurel size={34} color={ARENA.arenaText} className="mx-auto mb-1" />
            <p className="font-display text-[10px] font-black uppercase tracking-[0.25em]"
              style={{ color: ARENA.arenaText }}>Duel Challenge</p>
          </div>

          <div className="flex items-center justify-center gap-4 mt-5">
            <Fighter img={inv.image_url} name={inv.hero_name} sub={`@${inv.username}`}
              extra={`L${inv.level} · ${inv.elo}`} glow={ot.hex}
              pet={inv.pet_image ? { image: inv.pet_image, name: inv.pet_name } : null} />

            <VersusEmblem active color={ARENA.goldFrame} />

            <Fighter img={hero?.image_url} name={hero?.name} sub="You"
              extra={hero ? `L${hero.level} · ${hero.elo}` : '—'} glow={t.hex} />
          </div>

          {inv.stake > 0 && (
            <div className="mt-5 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2"
                style={{
                  borderRadius: 'var(--radius)',
                  background: 'var(--accent-soft)',
                  border: `1px solid ${ARENA.gold}55`,
                }}>
                <Gem size={15} style={{ color: ARENA.arenaText }} />
                <span className="font-display font-black text-white">
                  {inv.stake} points at stake
                </span>
              </div>
            </div>
          )}
        </ArenaFloor>

        <div className="p-4" style={{ background: 'var(--surface-alt)' }}>
          {!hero ? (
            <>
              <p className="text-sm text-center mb-3" style={{ color: 'var(--text-soft)' }}>
                You need a hero to fight.
              </p>
              <Link to="/join" className="btn-primary w-full">Create a hero</Link>
            </>
          ) : (
            <>
              <p className="text-sm text-center mb-3" style={{ color: 'var(--text-soft)' }}>
                <b>@{inv.username}</b> challenges you to a duel.
                {inv.stake > 0 && <> Winner takes <b>{inv.stake * 2} points</b>.</>}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => decline.mutate()} disabled={busy}
                  className="btn-ghost">Decline</button>
                <button onClick={() => accept.mutate()} disabled={busy || accept.isPending}
                  className="btn-primary">
                  {busy || accept.isPending
                    ? <><Loader2 size={15} className="animate-spin" /> Entering…</>
                    : <><Swords size={15} /> Fight!</>}
                </button>
              </div>
              <p className="text-[11px] text-center mt-3" style={{ color: 'var(--text-muted)' }}>
                Costs 1 arena stamina
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Fighter({ img, name, sub, extra, glow, pet }) {
  return (
    <div className="text-center">
      <Pedestal glow={glow} width="80%">
        <div className="w-24 h-32 rounded-xl overflow-hidden"
          style={{ padding: 2, background: ARENA.goldFrame, boxShadow: `0 0 20px -6px ${glow}` }}>
          <div className="w-full h-full rounded-[10px] overflow-hidden bg-black/40">
            {img && <img src={img} alt="" className="w-full h-full object-cover object-top" />}
          </div>
        </div>
      </Pedestal>

      {pet && (
        <div className="w-9 h-9 rounded-lg overflow-hidden mx-auto -mt-3 relative z-10"
          style={{ padding: 1.5, background: ARENA.goldFrame }}>
          <div className="w-full h-full rounded overflow-hidden bg-black/40">
            <img src={pet.image} alt="" className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      <p className="font-display font-bold text-xs mt-2 text-white truncate max-w-[96px]">
        {name || '—'}
      </p>
      <p className="text-[10px]" style={{ color: ARENA.arenaTextSoft }}>{sub}</p>
      <p className="text-[9px]" style={{ color: ARENA.arenaTextSoft, opacity: .7 }}>{extra}</p>
    </div>
  );
}